import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import translations from '../components/translations.json';

const API_BASE_URL = '/api';
const SUPPORTED_LANGS = ['fr', 'de', 'en'];

const getInitialLang = () => {
  const saved = localStorage.getItem('language');
  if (SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browser) ? browser : 'fr';
};

const TermsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [lang, setLang] = useState(getInitialLang);
  const [bodyHtml, setBodyHtml] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const t = translations[lang]?.terms || translations.fr.terms;

  // Inject <style> extracted from CGU HTML into document.head
  // (styles inside dangerouslySetInnerHTML are not reliably applied by browsers)
  useEffect(() => {
    if (!bodyHtml) return;
    return () => {
      const existing = document.getElementById('cgu-injected-styles');
      if (existing) existing.remove();
    };
  }, [bodyHtml]);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    setLoading(true);
    setNotFound(false);

    // Direct fetch using slug — CGU table has a Slug field
    fetch(`${API_BASE_URL}/cgu/${encodeURIComponent(slug)}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data && data.data.html) {
          setTitle(data.data.title || '');

          const raw = data.data.html;

          // Extract <style>...</style> block and inject it into <head>
          const styleMatch = raw.match(/<style>([\s\S]*?)<\/style>/i);
          if (styleMatch) {
            // Remove any previously injected style
            const prev = document.getElementById('cgu-injected-styles');
            if (prev) prev.remove();

            const styleEl = document.createElement('style');
            styleEl.id = 'cgu-injected-styles';
            styleEl.textContent = styleMatch[1];
            document.head.appendChild(styleEl);
          }

          // Strip the <style> block from the HTML before rendering
          setBodyHtml(raw.replace(/<style>[\s\S]*?<\/style>/i, ''));
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleLangChange = (l) => {
    setLang(l);
    localStorage.setItem('language', l);
  };

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(slug ? `/event/${slug}` : '/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <header className="bg-gray-900 py-4 px-6 flex items-center justify-between shadow">
        <h1 className="text-white font-bold text-lg truncate pr-4">
          {title || t.title}
        </h1>

        {/* Language switcher */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {SUPPORTED_LANGS.map(l => (
            <button
              key={l}
              onClick={() => handleLangChange(l)}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                lang === l ? 'bg-white text-gray-900' : 'text-white/60 hover:text-white'
              }`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">

        {/* Back / Close button */}
        <button
          onClick={handleBack}
          className="mb-6 text-sm text-blue-600 hover:underline font-medium"
        >
          {t.back}
        </button>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-500">{t.loading}</span>
          </div>
        )}

        {/* Not found */}
        {!loading && notFound && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center text-gray-600">
            {t.not_found}
          </div>
        )}

        {/* CGU HTML content — style is injected into <head>, only body HTML here */}
        {!loading && !notFound && bodyHtml && (
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-gray-400 border-t">
        Powered by <span className="font-semibold">Mobivent</span>
      </footer>
    </div>
  );
};

export default TermsPage;
